/**
 * Offline sync driver. Watches navigator.onLine, replays queued address
 * drafts and Quick Find scans to the backend, and emits progress events
 * the React app can subscribe to.
 */
import api from "@/lib/api";
import { mutate as swrMutate } from "swr";
import {
  listAddressQueue, removeAddressFromQueue,
  listScanQueue, removeScanFromQueue,
  dataUrlToBlob,
} from "@/lib/offlineDb";

const listeners = new Set();
let syncing = false;

export function onSyncEvent(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}
function emit(evt) { listeners.forEach((l) => { try { l(evt); } catch { /* listener error */ } }); }

export async function getQueueSummary() {
  const [addrs, scans] = await Promise.all([listAddressQueue(), listScanQueue()]);
  return { addresses: addrs.length, scans: scans.length };
}

export async function syncNow() {
  if (syncing) return { skipped: true };
  if (!navigator.onLine) return { offline: true };
  syncing = true;
  emit({ type: "sync-start" });
  let processed = 0, failed = 0;
  try {
    // 1) Replay queued manual addresses
    const drafts = await listAddressQueue();
    for (const draft of drafts) {
      try {
        const { data } = await api.post("/address-finder", draft.payload);
        for (const f of draft.files || []) {
          try {
            const blob = dataUrlToBlob(f.dataUrl);
            const fd = new FormData();
            fd.append("file", new File([blob], f.name || "photo.jpg", { type: f.type || "image/jpeg" }));
            await api.post(`/address-finder/${data.id}/images?image_type=entrance`, fd,
              { headers: { "Content-Type": "multipart/form-data" } });
          } catch (imgErr) {
            console.warn("offline image upload failed", imgErr);
          }
        }
        await removeAddressFromQueue(draft.id);
        processed += 1;
        emit({ type: "sync-progress", kind: "address", id: draft.id, address_id: data.id });
      } catch (e) {
        failed += 1;
        console.warn("address sync failed", e);
      }
    }

    // 2) Replay queued Quick Find scans
    const scans = await listScanQueue();
    for (const scan of scans) {
      try {
        const blob = dataUrlToBlob(scan.file.dataUrl);
        const fd = new FormData();
        fd.append("file", new File([blob], scan.file.name || "label.jpg", { type: scan.file.type || "image/jpeg" }));
        const { data } = await api.post("/address-finder/scan-label", fd,
          { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 });
        await removeScanFromQueue(scan.id);
        processed += 1;
        emit({ type: "sync-progress", kind: "scan", id: scan.id, result: data });
      } catch (e) {
        failed += 1;
        console.warn("scan sync failed", e);
      }
    }
  } finally {
    syncing = false;
    emit({ type: "sync-done", processed, failed });
    // Force every SWR-cached URL starting with /address-finder to revalidate
    // so newly synced addresses appear in the list without a page reload.
    if (processed > 0) {
      try {
        swrMutate(
          (key) => typeof key === "string" && key.startsWith("/address-finder"),
          undefined,
          { revalidate: true }
        );
      } catch (e) { /* ignore */ }
      // Also dispatch a window event so any non-SWR listeners can react.
      window.dispatchEvent(new CustomEvent("myportal:synced", { detail: { processed, failed } }));
    }
  }
  return { processed, failed };
}

// Trigger a sync whenever the browser regains connectivity or the tab
// becomes visible. Also try once at boot.
export function initOfflineSync() {
  const trigger = () => { syncNow().catch(() => {}); };
  window.addEventListener("online", trigger);
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) trigger();
  });
  setTimeout(trigger, 3000);
}
